import { EditorSelection, type ChangeSpec, type EditorState, type Line, type TransactionSpec } from '@codemirror/state';

/**
 * Tab and Shift-Tab.
 *
 * With a selection, Tab indents every line it touches and Shift-Tab outdents
 * them; the selection keeps covering the same whole lines, so the keys can be
 * pressed repeatedly. With a bare caret, Tab indents a Markdown list item
 * (nesting it) and otherwise types an indent. Each press is one transaction,
 * so one undo reverts it.
 *
 * Plain text indents with a tab character — the file says exactly what was
 * typed. Markdown uses spaces: two, or three on a numbered item so the nested
 * content lines up with the item's text.
 */

export type IndentKind = 'md' | 'txt';

const LIST_ITEM = /^\s*(?:[-*+]|\d{1,9}[.)])\s/;
const ORDERED_ITEM = /^\s*\d{1,9}[.)]\s/;

function unitFor(kind: IndentKind, line: Line): string {
  if (kind === 'txt') return '\t';
  return ORDERED_ITEM.test(line.text) ? '   ' : '  ';
}

/** The lines a selection range touches; a range ending at a line's very start does not touch that line. */
function touchedLines(state: EditorState): Line[] {
  const seen = new Set<number>();
  const lines: Line[] = [];
  for (const range of state.selection.ranges) {
    const first = state.doc.lineAt(range.from);
    let last = state.doc.lineAt(range.to);
    if (!range.empty && range.to === last.from && last.number > first.number) last = state.doc.line(last.number - 1);
    for (let n = first.number; n <= last.number; n++) {
      if (seen.has(n)) continue;
      seen.add(n);
      lines.push(state.doc.line(n));
    }
  }
  return lines;
}

function remap(state: EditorState, changes: ReturnType<EditorState['changes']>): EditorSelection {
  return EditorSelection.create(
    state.selection.ranges.map((r) => {
      if (r.empty) return EditorSelection.cursor(changes.mapPos(r.head, 1));
      // A range that begins at a line start keeps beginning there, so whole lines stay selected.
      const atLineStart = (pos: number) => state.doc.lineAt(pos).from === pos;
      const map = (pos: number, isStart: boolean) => changes.mapPos(pos, isStart && atLineStart(pos) ? -1 : 1);
      const forward = r.anchor <= r.head;
      const anchor = map(r.anchor, forward);
      const head = map(r.head, !forward);
      return EditorSelection.range(anchor, head);
    }),
    state.selection.mainIndex,
  );
}

export function indentLines(state: EditorState, kind: IndentKind): TransactionSpec | null {
  const lines = touchedLines(state);
  const multi = lines.length > 1;
  const specs: ChangeSpec[] = [];
  for (const line of lines) {
    // Blank lines inside a block selection stay blank.
    if (multi && !line.text.trim()) continue;
    specs.push({ from: line.from, insert: unitFor(kind, line) });
  }
  if (!specs.length) return null;
  const changes = state.changes(specs);
  return { changes, selection: remap(state, changes), userEvent: 'input.indent' };
}

export function outdentLines(state: EditorState, kind: IndentKind): TransactionSpec | null {
  const specs: ChangeSpec[] = [];
  for (const line of touchedLines(state)) {
    const lead = /^[ \t]*/.exec(line.text)![0];
    if (!lead) continue;
    let remove: number;
    if (lead[0] === '\t') remove = 1;
    else remove = Math.min(lead.replace(/\t.*$/, '').length, kind === 'txt' ? 4 : unitFor(kind, line).length);
    if (remove > 0) specs.push({ from: line.from, to: line.from + remove });
  }
  if (!specs.length) return null;
  const changes = state.changes(specs);
  return { changes, selection: remap(state, changes), userEvent: 'delete.dedent' };
}

/** What Tab does at this selection. */
export function tabSpec(state: EditorState, kind: IndentKind): TransactionSpec | null {
  const hasSelection = state.selection.ranges.some((r) => !r.empty);
  const onListItem = kind === 'md' && state.selection.ranges.every((r) => LIST_ITEM.test(state.doc.lineAt(r.head).text));
  if (hasSelection || onListItem) return indentLines(state, kind);
  return { ...state.replaceSelection(kind === 'txt' ? '\t' : '  '), userEvent: 'input' };
}
